// Extracted from Dashboard/index.web.jsx on 2026-10-02.
import { useMemo } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import AddWidgetComponent from '../AddWidgetComponent/AddWidgetComponent';
import WidgetContainer from '../WidgetContainer/WidgetContainer';
import dashboardConfig from '../../config/dashboard.config';
import type { DashboardGridProps } from '../../types/dashboard.types';
import { createDashboardLayouts } from '../../utils/dashboardLayout.utils';
import {
  isMapWidgetDefinition,
  isChartWidgetDefinition,
  isConnectedDevicesListWidgetDefinition,
  getOverwatchWidgetResizeProfile,
} from '../../utils/widgetSizing.utils';

const ResponsiveGridLayout = WidthProvider(Responsive);
const DASHBOARD_COLS_BY_BREAKPOINT = dashboardConfig['columns-by-breakpoint'];

const DashboardGrid = ({
  list,
  breakpoint: bp,
  onBreakpointChange,
  definedWidgetData,
  search,
  accessLevel,
  canEditWidgets,
  dashboardRowHeight,
  dashboardGridRef,
  onListChange,
  removeWidget,
  persistGridLayoutChanges,
  onOpenWidgetSelector,
}: DashboardGridProps) => {
  const layouts = useMemo(
    () => createDashboardLayouts({ list, definedWidgetData, canEditWidgets }),
    [list, definedWidgetData, canEditWidgets],
  );
  // Memoized so the widget subtrees only rebuild when something that actually
  // affects them changes (list/layout/sizing/perms/search), not on every
  // Dashboard render caused by alarm/alert polling and other header state.
  const widgetChildren = useMemo(() => {
    if (!list) return null;
    return list.map((item, index) => {
      const currentBreakpointLayout = layouts?.[bp] || [];
      const currentLayoutItem = currentBreakpointLayout.find(
        (layoutItem) => layoutItem.i === String(index),
      );
      const resolvedW = Number(currentLayoutItem?.w) || Number(item?.w) || 1;
      const resolvedH = Number(currentLayoutItem?.h) || Number(item?.h) || 1;
      const widgetOnSmallScreen = ['xs', 'sm', 'md'].includes(bp) && resolvedW > 1;
      if (widgetOnSmallScreen) {
        return null;
      }
      const widgetDefinition = definedWidgetData[item.id];

      if (!widgetDefinition) {
        return null;
      }
      const widgetSizeKey = `${resolvedW}x${resolvedH}`;

      const activeCols = DASHBOARD_COLS_BY_BREAKPOINT[bp] ?? DASHBOARD_COLS_BY_BREAKPOINT.xxl;
      const allowExpandedMapSize =
        isMapWidgetDefinition(widgetDefinition) &&
        resolvedW >= 1 &&
        resolvedW <= activeCols &&
        resolvedH >= 1 &&
        resolvedH <= 2;
      const allowExpandedChartSize =
        isChartWidgetDefinition(widgetDefinition) &&
        resolvedW >= Math.min(2, activeCols) &&
        resolvedW <= Math.min(4, activeCols) &&
        resolvedH >= 1 &&
        resolvedH <= 2;
      const allowExpandedConnectedDevicesListSize =
        isConnectedDevicesListWidgetDefinition(widgetDefinition) &&
        resolvedW >= 1 &&
        resolvedW <= Math.min(2, activeCols) &&
        resolvedH === 2;
      const overwatchResizeProfile = getOverwatchWidgetResizeProfile(widgetDefinition, activeCols);
      const allowExpandedOverwatchWidgetSize =
        Boolean(overwatchResizeProfile) &&
        resolvedW >= overwatchResizeProfile.minW &&
        resolvedW <= overwatchResizeProfile.maxW &&
        resolvedH >= overwatchResizeProfile.minH &&
        resolvedH <= overwatchResizeProfile.maxH &&
        !(overwatchResizeProfile.disallowOneByOne && resolvedW === 1 && resolvedH === 1);
      const showResizeIndicator =
        canEditWidgets &&
        (item.isDraggable ?? true) &&
        (isMapWidgetDefinition(widgetDefinition) ||
          isChartWidgetDefinition(widgetDefinition) ||
          isConnectedDevicesListWidgetDefinition(widgetDefinition) ||
          Boolean(overwatchResizeProfile));
      if (
        widgetDefinition.sizes &&
        !widgetDefinition.sizes[widgetSizeKey] &&
        !allowExpandedMapSize &&
        !allowExpandedChartSize &&
        !allowExpandedConnectedDevicesListSize &&
        !allowExpandedOverwatchWidgetSize
      ) {
        return null;
      }
      if (!widgetDefinition.sidebarData?.accessLevel) {
        console.warn('Widget definition missing sidebarData.assessLevel', widgetDefinition);
        return null;
      }
      if (!widgetDefinition.sidebarData.accessLevel.includes(accessLevel)) return null;

      if (item?.displayed === false || item?.active === false) return null;

      if (
        !widgetDefinition.name
          .replace(' ', '')
          .toLowerCase()
          .includes(search.replace(' ', '').toLowerCase())
      )
        return null;

      return (
        <div key={String(index)}>
          <WidgetContainer
            key={String(index)}
            item={{
              ...widgetDefinition,
              ...item,
              w: resolvedW,
              h: resolvedH,
            }}
            index={index}
            expandable
            uninteractable={undefined}
            scale={undefined}
            list={list}
            onListChange={onListChange}
            removeWidget={removeWidget}
            lockedFromRole={item?.lockedFromRole || false}
            gridRowHeight={dashboardRowHeight}
            showResizeIndicator={showResizeIndicator}
            isPopoutEnabled={['lg', 'xl', 'xxl'].includes(bp)}
          />
        </div>
      );
    });
  }, [
    list,
    bp,
    layouts,
    definedWidgetData,
    search,
    accessLevel,
    canEditWidgets,
    dashboardRowHeight,
    onListChange,
    removeWidget,
  ]);

  return (
    <div className="w-100" ref={dashboardGridRef}>
      <ResponsiveGridLayout
        // Bootstrap breakpoints
        breakpoints={dashboardConfig.breakpoints}
        cols={dashboardConfig['columns-by-breakpoint']}
        rowHeight={dashboardRowHeight}
        margin={[dashboardConfig['grid-gap'], dashboardConfig['grid-gap']]}
        layouts={layouts}
        isResizable={canEditWidgets}
        onBreakpointChange={onBreakpointChange}
        onDragStop={(gridLayout) => persistGridLayoutChanges(gridLayout, { includeSize: false })}
        onResizeStop={(gridLayout) => persistGridLayoutChanges(gridLayout, { includeSize: true })}
        draggableHandle=".handle"
        style={{
          alignItems: 'start',
          marginLeft: '-10px',
        }}
      >
        {widgetChildren}
        {canEditWidgets && (
          <div
            key="add"
            className="rounded-2 overflow-hidden"
            style={{
              width: '100%',
              height: '100%',
              backgroundColor: 'var(--color-base)',
            }}
          >
            <AddWidgetComponent openWidgetSelector={onOpenWidgetSelector} />
          </div>
        )}
      </ResponsiveGridLayout>
    </div>
  );
};

export default DashboardGrid;
